import React, { useState } from 'react';
import { navigation } from '../data';
import { motion } from 'framer-motion';
import Socials from './Socials';
import { Link } from 'react-scroll';
import { MenuAlt3Icon, XIcon } from '@heroicons/react/outline';
import { hapticLight, hapticTap } from '../utils/haptics';

const MobileNav = () => {
  const [isOpen, setIsOpen] = useState(false);

  // Tween (not spring) so the animation has a fixed duration and no
  // overshoot. Springs add extra oscillation frames at the end, which on
  // mobile = more frames of expensive compositing = more visible stutter.
  const circleVariants = {
    hidden: {
      scale: 0,
      transition: { duration: 0.3, ease: [0.4, 0, 1, 1] },
    },
    visible: {
      scale: 180,
      transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
    },
  };

  const ulVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delay: 0,
      },
    },
  };

  // Blur layer fades in AFTER the disc has mostly expanded. Filters are
  // expensive to re-sample on mobile, but a static fullscreen blur that
  // only animates opacity is cheap because the filter region never
  // changes size.
  const blurVariants = {
    hidden: { opacity: 0, transition: { duration: 0.2 } },
    visible: { opacity: 1, transition: { delay: 0.25, duration: 0.3 } },
  };

  // Prevent body scroll when menu is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  return (
    <nav className='relative z-[200]'>
      <div
        onClick={() => { hapticLight(); setIsOpen(true); }}
        className={`cursor-pointer text-white p-4 rounded-full hover:bg-white/10 transition-colors duration-300 relative z-[200] ${isOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
      >
        <MenuAlt3Icon className='w-8 h-8' />
      </div>

      {/* Expanding colored disc - cheap: solid color + transform only.
          No filters on this element while it scales. */}
      <motion.div
        variants={circleVariants}
        initial='hidden'
        animate={isOpen ? 'visible' : 'hidden'}
        className='w-4 h-4 rounded-full bg-accent/80 fixed top-[40px] right-[40px] z-[150]'
        style={{
          willChange: 'transform',
          transform: 'translateZ(0)',
          backfaceVisibility: 'hidden',
        }}
      ></motion.div>

      {/* Fullscreen blur layer - static size, only opacity animates.
          This is what gives the glassy look without re-sampling a
          growing filter region every frame. */}
      <motion.div
        variants={blurVariants}
        initial='hidden'
        animate={isOpen ? 'visible' : 'hidden'}
        className='fixed inset-0 backdrop-blur-lg pointer-events-none z-[148]'
        style={{ willChange: 'opacity' }}
      />

      {/* Subtle light effects - only mounted once the disc is already
          covering the viewport, so they don't fight for GPU cycles
          during the expansion. */}
      {isOpen && (
        <>
          <div className='fixed top-1/4 right-1/4 w-96 h-96 bg-white/5 rounded-full blur-2xl pointer-events-none z-[140]'></div>
          <div className='fixed bottom-1/3 left-1/3 w-64 h-64 bg-accent/5 rounded-full blur-2xl pointer-events-none z-[140]'></div>
        </>
      )}

      <motion.ul
        variants={ulVariants}
        initial='hidden'
        animate={isOpen ? 'visible' : ''}
        className={`${
          isOpen ? 'right-0' : '-right-full'
        } fixed top-0 bottom-0 w-full min-h-screen flex flex-col justify-center items-center transition-all duration-300 overflow-y-auto py-20 z-[150]`}
        style={{ height: '100vh' }}
      >
        <div
          onClick={() => { hapticLight(); setIsOpen(false); }}
          className='cursor-pointer fixed top-8 left-1/2 -translate-x-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-colors duration-300 z-[200]'
        >
          <XIcon className='w-8 h-8' />
        </div>
        {navigation.map((item, idx) => {
          return (
            <li key={idx} className='mb-8'>
              <Link
                onClick={() => { hapticTap(); setIsOpen(false); }}
                to={item.href}
                smooth={true}
                duration={500}
                offset={-70}
                className='text-xl cursor-pointer capitalize relative group'
              >
                {item.name}
                <span className='absolute left-0 bottom-0 w-0 h-0.5 bg-white/40 group-hover:w-full transition-all duration-300'></span>
              </Link>
            </li>
          );
        })}
        <div className='mt-8 p-6 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10'>
          <Socials />
        </div>
      </motion.ul>
    </nav>
  );
};

export default MobileNav;
